import {areSetsEqual} from '@benchling/js-utils';
import {produce} from 'immer';
import React from 'react';
import {assign, setup, fromPromise, assertEvent} from 'xstate';

import {ANTIBODY_CONSTANT_DOMAIN_TYPES} from 'antibodies/AntibodyConstants';
import {distinctDomainToKey, formatToSortedDistinctDomainsWithChainIndex} from 'antibodies/AntibodyUtils';
import {areConstantDomainsFullyDefined} from 'antibodies/nested-data-creation/AntibodyNestedDataCreationUtil';
import {
  AntibodyDomainPairInput,
  AntibodyNestedDataCreationState,
  AntibodyDomainInput,
  INITIAL_ANTIBODY_NESTED_DATA_CREATION_STATE,
  NDCSchemaConfig,
  TranslationParameters,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import {
  nestedCreateAndRegisterAntibodies,
  NestedCreateAndRegisterAntibodiesInput,
} from 'antibodies/nested-data-creation/NestedDataCreationDAO';
import {assert} from 'Asserts';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {MakeMaybe} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import {AntibodyFormatGraphQL} from '../../../graphql-fragments/antibodies/__generated/AntibodyFormatGraphQL';

/** The outer array represents each antibody, the inner array contains all domain pairs for that antibody. */
export type SelectDomainPairsInput = Array<Array<AntibodyDomainPairInput>>;

type AntibodyNestedDataCreationModalEvents =
  | {type: 'next'}
  | {type: 'back'}
  | {
      type: 'selectRegistryAndDependentConfig';
      registryId: string | null;
      ndcSchemaConfig: NDCSchemaConfig;
    }
  | {type: 'selectFormat'; format: AntibodyFormatGraphQL | null}
  | {type: 'selectSchema'; schemaId: string | null}
  | {type: 'setProjectFolder'; projectFolderId: string}
  | {type: 'setTranslationParameters'; translationParameters: TranslationParameters}
  | {type: 'selectConstantDomains'; constantDomainEntities: Array<AntibodyDomainInput>}
  | {type: 'selectDomainPairs'; selectDomainPairsInput: SelectDomainPairsInput}
  | {type: 'createAndRegister'};

type HandleCreateAntibodiesInput = MakeMaybe<
  NestedCreateAndRegisterAntibodiesInput,
  'projectFolderId' | 'antibodySchemaId'
>;

async function handleCreateAntibodies(input: HandleCreateAntibodiesInput): Promise<void> {
  assert(input.projectFolderId != null, 'Expected a project folder ID');
  assert(input.antibodySchemaId != null, 'Expected an antibody schema ID');
  try {
    LoadingScreen.show('Creating antibodies from DNA pairs...');
    // We cannot pass `input` directly because they're maybe'd in HandleCreateAntibodiesInput, and need to be
    // passed in separately after the assertions.
    const result = await nestedCreateAndRegisterAntibodies({
      ...input,
      projectFolderId: input.projectFolderId,
      antibodySchemaId: input.antibodySchemaId,
    });
    if (result.ndcWorkspaceUrl) {
      notifications.info(
        <>
          <p>We're working on creating antibodies and components...</p>
          <a href={result.ndcWorkspaceUrl}>View progress</a>
        </>
      );
    } else {
      notifications.info("We're working on creating antibodies and components...");
    }
  } catch (e) {
    notifications.notifyError(e);
    throw e;
  } finally {
    LoadingScreen.hide();
  }
}

/**
 * State machine handling the progress and state of configuring antibody nested data creation in a modal.
 */
const AntibodyNestedDataCreationModalMachine = setup({
  types: {
    context: {} as AntibodyNestedDataCreationState,
    events: {} as AntibodyNestedDataCreationModalEvents,
    input: {} as {initialProjectFolderId?: string | null},
  },
  actors: {
    submit: fromPromise(async ({input}: {input: HandleCreateAntibodiesInput}) => {
      await handleCreateAntibodies(input);
      return {success: true};
    }),
  },
  actions: {
    selectRegistryAndDependentConfig: assign(({context, event}) => {
      assertEvent(event, 'selectRegistryAndDependentConfig');
      return produce(context, (draft) => {
        draft.selectedRegistryId = event.registryId;
        draft.ndcSchemaConfig = event.ndcSchemaConfig;
      });
    }),
    selectFormat: assign(({context, event}) => {
      assertEvent(event, 'selectFormat');
      const newFormatId = event.format?.id ?? null;
      if (context.selectedFormatId === newFormatId) {
        return context;
      }

      return produce(context, (draft) => {
        draft.selectedFormatId = newFormatId;
        if (event.format != null) {
          // When a format is selected, prepopulate the constant domain entities with all required constant domains for that format
          const constantDomains = Object.values(
            formatToSortedDistinctDomainsWithChainIndex(event.format)
          ).filter((domain) => ANTIBODY_CONSTANT_DOMAIN_TYPES.includes(domain.type));
          draft.inputConfig.constantDomains = constantDomains.map((constantDomain) => ({
            componentFormatKey: constantDomain,
            dnaComponent: null,
            aaComponent: null,
          }));
        } else {
          draft.inputConfig.constantDomains = [];
        }
      });
    }),
    selectSchema: assign(({context, event}) => {
      assertEvent(event, 'selectSchema');
      return produce(context, (draft) => {
        draft.selectedSchemaId = event.schemaId;
      });
    }),
    selectDomainPairs: assign(({context, event}) => {
      assertEvent(event, 'selectDomainPairs');
      return produce(context, (draft) => {
        // If antibodyCreateInputs not null and not empty, assumes that the incoming order of domain pairs
        // matches the order of antibodyCreateInputs
        if (
          draft.inputConfig.antibodyCreateInputs != null &&
          draft.inputConfig.antibodyCreateInputs.length > 0
        ) {
          // If antibodyCreateInputs is not empty, we have to consider the possibility of changing the number
          // of antibodies. If we're getting more domain pairs than antibodies, we need to create new
          // antibodies. If we're getting fewer domain pairs than antibodies, we leave the last antibodies
          // alone.
          if (event.selectDomainPairsInput.length > draft.inputConfig.antibodyCreateInputs.length) {
            draft.inputConfig.antibodyCreateInputs = event.selectDomainPairsInput.map(
              (domainPairs, index) => ({
                ...(index < draft.inputConfig.antibodyCreateInputs.length
                  ? draft.inputConfig.antibodyCreateInputs[index]
                  : {domains: null, chains: null}),
                pairs: domainPairs,
              })
            );
          } else {
            draft.inputConfig.antibodyCreateInputs = draft.inputConfig.antibodyCreateInputs.map(
              (antibodyCreateInput, index) => ({
                ...antibodyCreateInput,
                pairs:
                  index < event.selectDomainPairsInput.length ? event.selectDomainPairsInput[index] : null,
              })
            );
          }
        } else {
          // If antibodyCreateInputs is empty, create a new antibody for each array of domain pairs
          draft.inputConfig.antibodyCreateInputs = event.selectDomainPairsInput.map((domainPairs) => ({
            pairs: domainPairs,
            domains: null,
            chains: null,
          }));
        }
      });
    }),
    setTranslationParameters: assign(({context, event}) => {
      assertEvent(event, 'setTranslationParameters');
      return produce(context, (draft) => {
        draft.translationParameters = event.translationParameters;
      });
    }),
    selectConstantDomains: assign(({context, event}) => {
      assertEvent(event, 'selectConstantDomains');
      // Since selectFormat prepopulates all the constant domains on a format, this assertion ensures that the
      // required constant domains are not changed unexpectedly.
      const eventConstantDomainsKeys = new Set(
        event.constantDomainEntities.map((constantDomain) =>
          distinctDomainToKey(constantDomain.componentFormatKey)
        )
      );
      assert(
        eventConstantDomainsKeys.size === event.constantDomainEntities.length,
        'Expected constant domain entities to not have duplicate keys'
      );
      assert(
        areSetsEqual(
          eventConstantDomainsKeys,
          new Set(
            context.inputConfig.constantDomains.map((constantDomain) =>
              distinctDomainToKey(constantDomain.componentFormatKey)
            )
          )
        ),
        'Number of constant domain entities should not change on selection'
      );
      return produce(context, (draft) => {
        draft.inputConfig.constantDomains = event.constantDomainEntities;
      });
    }),
    setProjectFolder: assign(({context, event}) => {
      assertEvent(event, 'setProjectFolder');
      return produce(context, (draft) => {
        draft.selectedProjectFolderId = event.projectFolderId;
      });
    }),
  },
}).createMachine({
  context: ({input}) =>
    produce(INITIAL_ANTIBODY_NESTED_DATA_CREATION_STATE, (draft) => {
      draft.selectedProjectFolderId =
        input.initialProjectFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY) ?? null;
    }),
  id: 'antibodyNestedDataCreationModal',
  initial: 'selectFormat',
  states: {
    selectFormat: {
      initial: 'defineCreation',
      on: {
        next: {
          target: 'defineDomains',
          guard: ({context}) =>
            context.selectedRegistryId != null &&
            context.selectedFormatId != null &&
            context.selectedSchemaId != null &&
            context.ndcSchemaConfig.dnaPairSchemaId != null &&
            context.ndcSchemaConfig.aaDomainSchemaId != null,
        },
      },
      states: {
        defineCreation: {
          on: {
            selectRegistryAndDependentConfig: {actions: 'selectRegistryAndDependentConfig'},
            selectFormat: {actions: 'selectFormat'},
            selectSchema: {actions: 'selectSchema'},
          },
        },
      },
    },
    defineDomains: {
      initial: 'defineDomains',
      on: {
        next: {
          target: 'finalize',
          guard: ({context}) =>
            context.inputConfig.antibodyCreateInputs.every(
              (input) => input.pairs != null && input.pairs.length > 0
            ) &&
            // Checks that constant domains are fully defined iff they're being shared
            (!context.inputConfig.isSharingConstantDomains ||
              areConstantDomainsFullyDefined(context.inputConfig.constantDomains)),
        },
        back: {
          target: 'selectFormat',
        },
      },
      states: {
        defineDomains: {
          on: {
            setTranslationParameters: {actions: 'setTranslationParameters'},
            selectDomainPairs: {actions: 'selectDomainPairs'},
            selectConstantDomains: {actions: 'selectConstantDomains'},
          },
        },
      },
    },
    finalize: {
      initial: 'finalizeStep',
      on: {
        back: {
          target: 'defineDomains',
        },
        createAndRegister: {
          target: 'saving',
        },
      },
      states: {
        finalizeStep: {
          on: {
            setProjectFolder: {actions: 'setProjectFolder'},
          },
        },
      },
    },
    saving: {
      invoke: {
        src: 'submit',
        input: ({context}: {context: AntibodyNestedDataCreationState}): HandleCreateAntibodiesInput => {
          let antibodyCreateInputs = context.inputConfig.antibodyCreateInputs;
          if (context.inputConfig.isSharingConstantDomains) {
            assert(
              areConstantDomainsFullyDefined(context.inputConfig.constantDomains),
              'Expected an entity for every constant domain'
            );
            antibodyCreateInputs = context.inputConfig.antibodyCreateInputs.map((input) => ({
              ...input,
              // If constant domains are being shared, add them to each antibodyCreateInput's domains field.
              // Note if we're using chains or pairs, domains could be null.
              domains: [...(input.domains || []), ...context.inputConfig.constantDomains],
            }));
          }

          return {
            projectFolderId: context.selectedProjectFolderId,
            antibodySchemaId: context.selectedSchemaId,
            antibodyCreateInputs,
            translationParameters: context.translationParameters,
          };
        },
        onDone: {
          target: 'done',
        },
        onError: {
          target: 'finalize',
        },
      },
    },
    done: {
      type: 'final',
    },
  },
});

export default AntibodyNestedDataCreationModalMachine;
